/**
 * 📮 **못 보낸 것을 들고 있다가, 인터넷이 돌아오면 보낸다.**
 *
 * ## 왜 필요한가 — 화면이 하던 약속을 코드가 안 지키고 있었다
 * 아이 화면은 실패했을 때 **「반에는 나중에 올라가요」**라고 말했다.
 * 그런데 그 「나중에」를 만드는 코드가 **없었다**(2026-08-26 발견).
 * 다시 시도하는 길도, 쌓아 두는 길도 없어서 실패한 글은 아이가 **다시 타자를 칠 때까지**
 * 그 태블릿 안에만 있었다.
 *
 * 장면: 마지막 문장을 쓰고 태블릿을 덮는다. 그 순간 학교 와이파이가 흔들렸다.
 * 아이 화면은 「저장했어요」. 선생님 화면에는 **그 아이만 0쪽.**
 * 수업이 끝나고 나서야 안다 — 그리고 그 글은 영영 안 올라간다.
 *
 * **학교 와이파이가 흔들리는 것은 사고가 아니라 이 수업의 상수다.**
 *
 * ## 🔴 `localStorage` 다 — 탭이 닫혀도 남아야 한다
 * 반 코드·선생님 열쇠는 탭에만 담지만(태블릿을 집에 가져가므로),
 * **못 보낸 글은 다르다.** 아이는 태블릿을 덮고 간다. 그게 바로 이걸 만든 이유다.
 * 다음 수업에 그 태블릿을 다시 켜면 그때 올라간다.
 * 담기는 것은 이미 이 기기에 저장돼 있는 것뿐이다(무수집 그대로 — 불변식 1).
 *
 * ## 🔴 같은 자리는 **덮어쓴다** — 쌓지 않는다
 * 한 아이의 그림책은 하나다. 세 번 실패했다고 세 번 보내면 낡은 글이 새 글을 덮는다.
 * 자리(`kind:반:아이`)마다 **가장 마지막 것 하나만** 들고 있는다.
 *
 * ## 🔴 서두르지 않는다 — 한 반 서른 대가 공유기 하나를 쓴다
 * 끊긴 동안 1초마다 두드리면 돌아온 순간 서른 대가 동시에 몰린다.
 * 5초 → 15초 → 45초 → 2분으로 늦춘다. 그리고 **인터넷이 돌아온 순간**과
 * **화면을 다시 볼 때**는 기다리지 않고 바로 보낸다(그게 대부분을 해결한다).
 */
import type { SavedHero, SavedStory } from "@greg-chick/shared";
import { pushStory, type PushResult } from "./storyApi";
import { joinClass } from "./classApi";

const KEY = "gc_outbox";
/** 자리마다 하나씩만 담기므로 원래 몇 개 안 된다. 그래도 한도는 둔다(고장 났을 때의 방어). */
const MAX = 40;
const BACKOFF_MS = [5_000, 15_000, 45_000, 120_000] as const;

/**
 * 🔴 **`seq` 로 비교한다 — 시각이 아니라.**
 *
 * 처음엔 `Date.now()` 로 「어느 것이 더 새 것인지」를 봤는데, 같은 밀리초에 두 번 담기면
 * 둘이 같아져서 **나중 글이 사라졌다**(검사가 잡았다). 학교 태블릿은 시각이 어긋나거나
 * 리셋되기도 한다 — 그때는 더 나빠진다. 순번은 이 기기 안에서만 오르므로 그런 일이 없다.
 * `at` 은 사람이 볼 때 쓰는 참고값일 뿐, 판정에는 안 쓴다.
 */
interface Base {
  cls: string;
  heroCode: string;
  seq: number;
  at: number;
}
type Body =
  | { kind: "story"; cls: string; heroCode: string; story: SavedStory }
  | { kind: "join"; cls: string; heroCode: string; hero: SavedHero };
type Item = Body & Base;

const slot = (i: Pick<Item, "kind" | "cls" | "heroCode">) => `${i.kind}:${i.cls}:${i.heroCode}`;

/* ── 담아 두는 곳 ───────────────────────────────────────── */

function read(): Item[] {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? "[]") as unknown;
    if (!Array.isArray(raw)) return [];
    // 지난 판에서 남은 것·손댄 것이 섞일 수 있다(불변식 7 — 밖에서 온 값)
    return raw.filter((x): x is Item => {
      if (!x || typeof x !== "object") return false;
      const i = x as Partial<Item>;
      return (
        (i.kind === "story" || i.kind === "join") &&
        typeof i.cls === "string" &&
        typeof i.heroCode === "string" &&
        typeof i.seq === "number" &&
        typeof i.at === "number"
      );
    });
  } catch {
    // 저장이 막힌 브라우저·깨진 값 — 이 화면 동안은 못 보낸 게 없는 셈 친다(경계 폴백)
    return [];
  }
}

function write(items: Item[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(items.slice(-MAX)));
  } catch {
    // 저장 공간이 꽉 찼거나 막혔다 — 여기서 던지면 아이가 글을 못 쓴다(경계 폴백)
  }
  for (const fn of watchers) fn(items.length);
}

/* ── 밖에서 쓰는 것 ─────────────────────────────────────── */

const watchers = new Set<(pending: number) => void>();

/** 못 보낸 개수가 바뀔 때 알려 준다(화면이 사실대로 말할 수 있게). */
export function onOutboxChange(fn: (pending: number) => void): () => void {
  watchers.add(fn);
  return () => watchers.delete(fn);
}

/**
 * 못 보낸 개수. `cls`·`heroCode` 를 주면 **그 자리 것만** 센다.
 *
 * 🔴 아이 화면은 반드시 좁혀서 물어야 한다. 태블릿에 지난 반의 밀린 것이 남아 있으면
 * 지금 이 아이는 잘 올라갔는데도 「아직 못 보냈어요」가 뜬다.
 */
/**
 * 마지막으로 **놓아 준** 때. 화면이 「이 반은 정리됐어요」를 말할 수 있게.
 * 0 이면 그런 일이 없었다.
 */
let lastGaveUp = 0;

export function outboxGaveUpAt(): number {
  return lastGaveUp;
}

export function outboxPending(cls?: string, heroCode?: string): number {
  const items = read();
  if (!cls) return items.length;
  return items.filter((x) => x.cls === cls && (!heroCode || x.heroCode === heroCode)).length;
}

/** 이 기기 안에서만 오르는 순번. 담겨 있는 것 중 가장 큰 것 다음. */
function nextSeq(items: Item[]): number {
  return items.reduce((m, x) => Math.max(m, x.seq), 0) + 1;
}

function enqueue(item: Body): void {
  const all = read();
  const full: Item = { ...item, seq: nextSeq(all), at: Date.now() };
  write([...all.filter((x) => slot(x) !== slot(full)), full]);
  void flushOutbox();
}

/** 그림책을 반에 올린다. 지금 못 보내면 들고 있다가 나중에 보낸다. */
export function queueStory(cls: string, heroCode: string, story: SavedStory): void {
  enqueue({ kind: "story", cls, heroCode, story });
}

/** 「왔어요」를 알린다. 못 보내면 들고 있는다 — 안 그러면 선생님 화면에 아예 안 뜬다. */
export function queueJoin(cls: string, hero: SavedHero): void {
  if (!hero.code) return;
  enqueue({ kind: "join", cls, heroCode: hero.code, hero });
}

/* ── 보내기 ─────────────────────────────────────────────── */

let sending = false;
let timer: number | undefined;
let tries = 0;

async function send(item: Item): Promise<PushResult> {
  return item.kind === "story"
    ? pushStory(item.cls, item.heroCode, item.story)
    : joinClass(item.cls, item.hero);
}

/**
 * 🔴 **못 보낼 것은 놓아 준다.**
 *
 * 예전엔 성패만 봐서, 서버가 「안 된다」고 해도 **2분마다 영영 두드렸다.**
 * 반이 지워졌거나 코드가 깨진 글은 절대 안 올라가는데, 아이 화면은
 * 「아직 못 보냈어요」로 영영 남고 학교 공유기만 두들긴다.
 *
 * 🔴 그래도 **글 자체는 안 지운다.** 우체통에서 빼는 것뿐이고,
 * 아이가 쓴 것은 이 기기(`gc_story_…`)에 그대로 있다.
 */
const OUTBOX_MAX_AGE_MS = 60 * 86_400_000; // 두 달 — 학기 하나보다 길게

function tooOld(item: Item, now: number): boolean {
  return now - item.at > OUTBOX_MAX_AGE_MS;
}

/**
 * 들고 있는 것을 보낸다. 남은 개수를 준다.
 *
 * 🔴 **성공한 것만 지운다.** 하나가 실패해도 나머지는 보낸다 —
 * 반이 다르거나(다음 주 수업) 종류가 다르면 서로 상관이 없다.
 */
export async function flushOutbox(): Promise<number> {
  if (sending) return outboxPending();
  const snapshot = read();
  if (snapshot.length === 0) {
    stopRetry();
    return 0;
  }
  sending = true;
  try {
    /** 더 안 들고 있을 자리와 **그때의 순번**. 순번까지 있어야 아래 한 줄이 성립한다. */
    const done = new Map<string, number>();
    const at = Date.now();
    let gaveUp = 0;
    for (const item of snapshot) {
      // 보내려는 사이에 더 새 글이 들어왔으면 이 낡은 것은 건너뛴다(낡은 글이 새 글을 덮지 않게)
      const cur = read().find((x) => slot(x) === slot(item));
      if (cur && cur.seq > item.seq) continue;
      // 두 달이 지나도록 못 보냈으면 놓아 준다 — 글은 이 기기에 그대로 있다
      if (tooOld(item, at)) {
        done.set(slot(item), item.seq);
        gaveUp++;
        continue;
      }
      const r = await send(item);
      if (r === "ok") done.set(slot(item), item.seq);
      else if (r === "gone") {
        // 🔴 서버가 「안 된다」고 했다 — 다시 해도 소용없다(반이 지워졌거나 코드가 깨졌다)
        done.set(slot(item), item.seq);
        gaveUp++;
      }
    }
    if (gaveUp > 0) lastGaveUp = at;
    /**
     * 🔴 보내는 사이에 아이가 계속 쓰고 있었을 수 있다. 그래서 **다시 읽어서** 거른다 —
     * 보낸 것보다 **새 것이면 남긴다.** 스냅샷을 그대로 저장하면 그 사이에 쓴 글이 사라진다.
     */
    const left = read().filter((x) => {
      const doneSeq = done.get(slot(x));
      return doneSeq === undefined || x.seq > doneSeq;
    });
    write(left);
    if (left.length > 0) scheduleRetry();
    else stopRetry();
    return left.length;
  } finally {
    sending = false;
  }
}

function scheduleRetry(): void {
  if (timer !== undefined) return;
  const wait = BACKOFF_MS[Math.min(tries, BACKOFF_MS.length - 1)]!;
  tries += 1;
  timer = setTimeout(() => {
    timer = undefined;
    void flushOutbox();
  }, wait) as unknown as number;
}

function stopRetry(): void {
  if (timer !== undefined) clearTimeout(timer);
  timer = undefined;
  tries = 0;
}

/**
 * 앱이 뜰 때 한 번 부른다.
 *
 * 🔴 **인터넷이 돌아온 순간**과 **화면을 다시 볼 때**가 제일 중요하다 —
 * 아이가 태블릿을 덮었다 다시 켜는 그 순간이 실제로 대부분을 해결한다.
 */
export function startOutbox(): void {
  if (typeof window === "undefined") return;
  const kick = () => void flushOutbox();
  window.addEventListener("online", kick);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") kick();
  });
  kick();
}

/** 검사용 — 담긴 것을 비운다. */
export function resetOutbox(): void {
  stopRetry();
  sending = false;
  lastGaveUp = 0;
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* 지울 것도 없다 */
  }
}
